import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Builder_, Checkbox_, device, Grey_, Grey_Link, IconSvg, Main_, Main_Plain, PageFull, PageTitle, Partial_Loader, Red_, Textarea_ } from "monica-alexandria";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import b from "../../services/api/Boards";
import l from "../../services/api/Landings";
import store from "../../redux/store";
import { useClickOutside } from "../../hooks/useClickOutside";
import { Calendar_Field } from "../../components/form/Calendar_Field";
import { CALL_FOLLOW_UP_FIELD, toDateTimeLocalValue } from "../crm/callFollowUp";

const PageWrap = styled(PageFull)`
  padding-bottom: 5rem;
`;

const CallsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;

  @media ${device.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const SummaryCard = styled.button`
  padding: 1.4rem;
  border: 1px solid ${(p) => p.active ? "var(--main)" : p.theme.low};
  border-radius: var(--smallRadius);
  background: ${(p) => p.active
    ? `color-mix(in srgb, var(--main) 9%, ${p.theme.mid})`
    : p.theme.mid};
  color: ${(p) => p.theme.color};
  text-align: left;
  cursor: pointer;

  strong, p { display: block; }
  strong { margin-top: 0.35rem; font-size: 2.2rem; }
  p { margin: 0; opacity: 0.68; }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const FilterChip = styled.button`
  padding: 0.75rem 1.1rem;
  border: 1px solid ${(p) => p.active ? "var(--main)" : p.theme.low};
  border-radius: 999px;
  background: ${(p) => p.active ? "var(--main)" : p.theme.mid};
  color: ${(p) => p.active ? "var(--white)" : p.theme.color};
  cursor: pointer;
`;

const CallGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const GroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  h3 { margin: 0; }
  p {
    margin: 0;
    min-width: 2.5rem;
    height: 2.5rem;
    padding: 0 0.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid color-mix(in srgb, var(--main) 38%, transparent);
    border-radius: 999px;
    background: color-mix(in srgb, var(--main) 16%, transparent);
    color: var(--main);
    font-family: "fontBold";
    font-size: 1.2rem;
    line-height: 1;
  }
`;

const CallCard = styled.article`
  position: relative;
  overflow: visible;
  display: grid;
  grid-template-columns: auto minmax(16rem, 1fr) minmax(18rem, 1.3fr);
  gap: 1.5rem;
  align-items: center;
  padding: 1.5rem;
  border: 1px solid ${(p) => p.theme.low};
  border-left: 4px solid ${(p) => p.state === "overdue" ? "#c95252" : p.state === "today" ? "#c77618" : "var(--main)"};
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};

  p { margin: 0; white-space: normal; overflow-wrap: anywhere; }

  @media ${device.md} {
    grid-template-columns: auto minmax(0, 1fr);

    > *:nth-child(n + 3) {
      grid-column: 1 / -1;
    }
  }
`;

const DateTile = styled.div`
  width: 6rem;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.background};
  text-align: center;

  p, strong { display: block; }
  p {
    margin: 0;
    padding: 0.35rem;
    background: ${(p) => p.theme.low};
    color: ${(p) => p.theme.color};
    text-transform: uppercase;
    font-weight: 700;
  }
  strong { padding: 0.65rem 0; color: ${(p) => p.theme.color}; font-size: 2rem; }
`;

const LeadDetails = styled.div`
  min-width: 0;

  h4, a {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  h4 { margin: 0 0 0.35rem; }
  a { color: var(--main); text-decoration: none; }
`;

const ContactLine = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.65rem;

  p { flex: 0 0 auto; }
  a { min-width: 0; }
  a::before {
    content: "·";
    margin-right: 0.65rem;
    color: ${(p) => p.theme.color};
    opacity: 0.45;
  }
`;

const Location = styled.div`
  min-width: 0;

  h5 {
    margin: 0 0 0.4rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const AgendaField = styled.div`
  position: relative;
  min-width: 0;
`;

const AgendaText = styled.p`
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  &:focus-visible {
    outline: 1px solid var(--main);
    outline-offset: 0.25rem;
  }
`;

const AgendaTooltip = styled.p`
  position: absolute;
  top: calc(100% + 0.6rem);
  left: 0;
  z-index: 40;
  width: max-content;
  max-width: min(42rem, calc(100vw - 4rem));
  padding: 0.9rem 1.1rem;
  display: none;
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.color};
  box-shadow: ${(p) => p.theme.out};
  white-space: normal !important;
  overflow-wrap: anywhere;
  pointer-events: none;

  ${AgendaField}:hover &,
  ${AgendaField}:focus-within & {
    display: block;
  }
`;

const CallTags = styled.div`
  min-width: 0;
  margin-top: 0.65rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ColumnTag = styled.p`
  display: block;
  min-width: 0;
  max-width: 100%;
  margin: 0 !important;
  padding: 0.3rem 0.75rem;
  overflow: hidden;
  white-space: nowrap !important;
  text-overflow: ellipsis;
  border: 1px solid ${(p) => p.$columnColor
    ? `color-mix(in srgb, ${p.$columnColor} 45%, transparent)`
    : p.theme.low};
  border-radius: 999px;
  background: ${(p) => p.$columnColor
    ? `color-mix(in srgb, ${p.$columnColor} 24%, transparent)`
    : p.theme.low};
  color: ${(p) => p.theme.color};
  font-size: 1.2rem;
  font-weight: 700;
`;

const PreviousOutcomeTag = styled.p`
  --call-outcome-color: ${(p) => p.$outcomeColor || "var(--main)"};
  display: block;
  max-width: 100%;
  margin: 0 !important;
  padding: 0.3rem 0.75rem;
  overflow: hidden;
  white-space: nowrap !important;
  text-overflow: ellipsis;
  border: 1px solid color-mix(in srgb, var(--call-outcome-color) 42%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--call-outcome-color) 15%, transparent);
  color: var(--call-outcome-color);
  font-size: 1.2rem;
  font-weight: 700;
`;

const CallActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.8rem;
  flex-wrap: wrap;
  padding-top: 1.25rem;
  border-top: 1px solid color-mix(in srgb, ${(p) => p.theme.color} 12%, transparent);

  @media ${device.md} {
    justify-content: stretch;

    > *:not([data-call-menu]) { flex: 1; justify-content: center; }
  }
`;

const CallMenuControl = styled.div.attrs({ "data-call-menu": true })`
  position: relative;
  flex: 0 0 auto;
`;

const CallMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.8rem);
  right: 0;
  z-index: 30;
  min-width: 16rem;
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};
  box-shadow: ${(p) => p.theme.out};
`;

const CallEditor = styled.div`
  grid-column: 1 / -1;
  padding-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  border-top: 1px solid color-mix(in srgb, ${(p) => p.theme.color} 12%, transparent);

  h4 { margin: 0; }
`;

const EditorFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  > *:last-child { grid-column: 1 / -1; }

  @media ${device.md} {
    grid-template-columns: 1fr;
    > *:last-child { grid-column: auto; }
  }
`;

const NativeDateTimeInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.color};
  box-shadow: ${(p) => p.theme.in};
  padding: var(--normalPads);
  font-size: var(--normalText);
`;

const OutcomeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.8rem;

  @media ${device.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const OutcomeOption = styled.button`
  min-height: 4.4rem;
  padding: 0.8rem 1rem;
  border: 1px solid ${(p) => p.selected ? "var(--main)" : p.theme.low};
  border-radius: var(--smallRadius);
  background: ${(p) => p.selected
    ? `color-mix(in srgb, var(--main) 12%, ${p.theme.background})`
    : p.theme.background};
  color: ${(p) => p.selected ? "var(--main)" : p.theme.color};
  font: inherit;
  font-weight: ${(p) => p.selected ? 700 : 500};
  cursor: pointer;

  p { margin: 0; }
`;

const EditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ActionError = styled.p`
  color: var(--error, #c95252);
`;

const EmptyCalls = styled.div`
  padding: 4rem 2rem;
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};
  text-align: center;

  svg { width: 3rem; fill: var(--main); }
`;

const ErrorMessage = styled.p`
  color: var(--error, #c95252);
`;

const InfiniteScrollSentinel = styled.div`
  min-height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GROUP_LABELS = {
  overdue: "Overdue",
  today: "Today",
  upcoming: "Upcoming",
};

const CALL_OUTCOME_LABELS = {
  connected: "Connected",
  no_answer: "No answer",
  voicemail: "Left voicemail",
  wrong_number: "Wrong number",
  cancelled: "Cancelled",
};

const CALL_OUTCOME_COLORS = {
  connected: "var(--main)",
  no_answer: "#c77618",
  voicemail: "#6f67c7",
  wrong_number: "#c95252",
  cancelled: "#c95252",
};

const readPreviousOutcome = (followUp = {}) => {
  const history = Array.isArray(followUp.history) ? followUp.history : [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const event = history[index];
    if (event?.type === "cancelled") return "cancelled";
    if (event?.type === "completed" && event?.outcome) return event.outcome;
  }
  return followUp.outcome || "";
};

const readLeadName = (lead = {}, fallback = "") => {
  return lead.FullName || [lead.Name, lead.Surname].filter(Boolean).join(" ") || lead.Email || fallback;
};

export default function CRM_CALLS() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [timing, setTiming] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [calls, setCalls] = useState([]);
  const [summary, setSummary] = useState({ total: 0, overdue: 0, today: 0, upcoming: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [openMenuLeadId, setOpenMenuLeadId] = useState("");
  const [activeAction, setActiveAction] = useState(null);
  const [editDraft, setEditDraft] = useState({ scheduledAt: "", note: "" });
  const [outcomeDraft, setOutcomeDraft] = useState({ outcome: "", note: "", scheduleAnother: false, scheduledAt: "" });
  const [savingAction, setSavingAction] = useState(false);
  const [actionError, setActionError] = useState("");
  const [useNativeDatePicker, setUseNativeDatePicker] = useState(false);
  const loadMoreSentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const requestVersionRef = useRef(0);
  const callMenuRef = useRef(null);

  const currentUser = store.getState()?.user?.user || {};
  const currentUserId = currentUser?.sub || "";
  const currentUserEmail = typeof currentUser?.email === "string"
    ? currentUser.email
    : currentUser?.email?.email || "";
  const currentUserName = [currentUser?.firstname, currentUser?.lastname].filter(Boolean).join(" ").trim() || currentUserEmail;

  useClickOutside(callMenuRef, () => setOpenMenuLeadId(""), Boolean(openMenuLeadId));

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(device.md.replace("@media ", ""));
    const updatePicker = () => setUseNativeDatePicker(media.matches);
    updatePicker();
    media.addEventListener("change", updatePicker);
    return () => media.removeEventListener("change", updatePicker);
  }, []);

  const loadFirstPage = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current;
    loadingMoreRef.current = false;
    setLoading(true);
    setError(false);
    const response = await b.getUserCalls({ timing, assignee, page: 0, limit: 30 });
    if (requestVersion !== requestVersionRef.current) return false;

    if (!response) {
      setCalls([]);
      setError(true);
      setLoading(false);
      return false;
    }

    setCalls(response?.data?.calls || []);
    setSummary(
      (assignee === "mine" ? response?.data?.mySummary : response?.data?.summary)
      || { total: 0, overdue: 0, today: 0, upcoming: 0 }
    );
    setHasMore(Boolean(response?.data?.hasMore));
    setPage(0);
    setLoading(false);
    return true;
  }, [assignee, timing]);

  useEffect(() => {
    loadFirstPage();
    return () => { requestVersionRef.current += 1; };
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || loading || !hasMore) return;
    loadingMoreRef.current = true;
    const requestVersion = requestVersionRef.current;
    const nextPage = page + 1;
    setLoading(true);
    const response = await b.getUserCalls({ timing, assignee, page: nextPage, limit: 30 });
    if (requestVersion !== requestVersionRef.current) return;
    if (!response) {
      setError(true);
      setHasMore(false);
      setLoading(false);
      loadingMoreRef.current = false;
      return;
    }
    setCalls((current) => [...current, ...(response?.data?.calls || [])]);
    setHasMore(Boolean(response?.data?.hasMore));
    setPage(nextPage);
    setLoading(false);
    loadingMoreRef.current = false;
  }, [assignee, hasMore, loading, page, timing]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!hasMore || !sentinel || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { rootMargin: "300px 0px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const groupedCalls = useMemo(() => calls.reduce((groups, call) => {
    const state = GROUP_LABELS[call?.state] ? call.state : "upcoming";
    groups[state].push(call);
    return groups;
  }, { overdue: [], today: [], upcoming: [] }), [calls]);

  const openBoard = (call) => {
    navigate(`/${lang || "en"}/crm/board/${call.questId}`, {
      state: {
        crmCallTarget: {
          questId: call.questId,
          columnId: call.columnId,
          lead: call.lead,
        },
      },
    });
  };

  const readActor = () => ({
    userId: currentUserId,
    email: currentUserEmail,
    name: currentUserName,
  });

  const suggestedNextCallDate = (call) => {
    const now = new Date();
    const currentDate = call?.followUp?.scheduledAt ? new Date(call.followUp.scheduledAt) : null;
    const nextDate = currentDate && !Number.isNaN(currentDate.getTime()) && currentDate > now
      ? new Date(currentDate)
      : now;
    nextDate.setDate(nextDate.getDate() + 1);
    nextDate.setSeconds(0, 0);
    nextDate.setMinutes(Math.ceil(nextDate.getMinutes() / 15) * 15);
    return nextDate.toISOString();
  };

  const persistCall = async (call, nextFollowUp) => {
    const lead = call?.lead || {};
    setSavingAction(true);
    setActionError("");

    try {
      const response = await l.editLead({
        _id: lead?._id,
        id: lead?.id,
        [CALL_FOLLOW_UP_FIELD]: {
          ...nextFollowUp,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUserId || currentUserEmail,
        },
      });

      if (!response?.data?.form) {
        throw new Error(response?.data?.error || "Call was not saved");
      }

      setActiveAction(null);
      setOpenMenuLeadId("");
      await loadFirstPage();
      return true;
    } catch (saveError) {
      console.error("Could not update call:", saveError);
      setActionError(t("Could not save the call. Please try again."));
      return false;
    } finally {
      setSavingAction(false);
    }
  };

  const openEditCall = (call) => {
    setOpenMenuLeadId("");
    setActionError("");
    setEditDraft({
      scheduledAt: call?.followUp?.scheduledAt || "",
      note: call?.followUp?.note || "",
    });
    setActiveAction({ leadId: String(call?.lead?._id || call?.lead?.id || ""), mode: "edit" });
  };

  const saveEditedCall = async (call) => {
    const nextDate = editDraft.scheduledAt ? new Date(editDraft.scheduledAt) : null;
    if (!nextDate || Number.isNaN(nextDate.getTime()) || nextDate.getTime() <= Date.now()) {
      setActionError(t("Choose a future date and time"));
      return;
    }

    const followUp = call?.followUp || {};
    const history = Array.isArray(followUp.history) ? [...followUp.history] : [];
    if (new Date(followUp.scheduledAt).getTime() !== nextDate.getTime()) {
      history.push({
        type: "rescheduled",
        fromScheduledAt: followUp.scheduledAt,
        toScheduledAt: nextDate.toISOString(),
        at: new Date().toISOString(),
        by: readActor(),
      });
    }

    await persistCall(call, {
      ...followUp,
      status: "scheduled",
      scheduledAt: nextDate.toISOString(),
      note: String(editDraft.note || "").trim(),
      history: history.slice(-50),
    });
  };

  const openFinishCall = (call) => {
    setOpenMenuLeadId("");
    setActionError("");
    setOutcomeDraft({ outcome: "", note: "", scheduleAnother: false, scheduledAt: "" });
    setActiveAction({ leadId: String(call?.lead?._id || call?.lead?.id || ""), mode: "finish" });
  };

  const chooseOutcome = (call, outcome) => {
    const shouldScheduleAnother = ["no_answer", "voicemail"].includes(outcome);
    setOutcomeDraft((draft) => ({
      ...draft,
      outcome,
      scheduleAnother: shouldScheduleAnother,
      scheduledAt: shouldScheduleAnother
        ? draft.scheduledAt || suggestedNextCallDate(call)
        : "",
    }));
    setActionError("");
  };

  const finishCall = async (call) => {
    if (!outcomeDraft.outcome) {
      setActionError(t("Choose a call outcome"));
      return;
    }

    const nextDate = outcomeDraft.scheduleAnother && outcomeDraft.scheduledAt
      ? new Date(outcomeDraft.scheduledAt)
      : null;
    if (outcomeDraft.scheduleAnother && (!nextDate || Number.isNaN(nextDate.getTime()) || nextDate.getTime() <= Date.now())) {
      setActionError(t("Choose a future date and time"));
      return;
    }

    const followUp = call?.followUp || {};
    const completedAt = new Date().toISOString();
    const history = [
      ...(Array.isArray(followUp.history) ? followUp.history : []),
      {
        type: "completed",
        outcome: outcomeDraft.outcome,
        scheduledAt: followUp.scheduledAt,
        note: String(outcomeDraft.note || "").trim(),
        at: completedAt,
        by: readActor(),
      },
    ];

    if (nextDate) {
      history.push({
        type: "scheduled",
        fromOutcome: outcomeDraft.outcome,
        toScheduledAt: nextDate.toISOString(),
        at: completedAt,
        by: readActor(),
      });
    }

    await persistCall(call, {
      ...followUp,
      status: nextDate ? "scheduled" : "completed",
      scheduledAt: nextDate ? nextDate.toISOString() : followUp.scheduledAt,
      completedAt: nextDate ? "" : completedAt,
      completedBy: nextDate ? "" : currentUserId || currentUserEmail,
      outcome: nextDate ? "" : outcomeDraft.outcome,
      outcomeNote: nextDate ? "" : String(outcomeDraft.note || "").trim(),
      history: history.slice(-50),
    });
  };

  const cancelCall = async (call) => {
    const followUp = call?.followUp || {};
    await persistCall(call, {
      ...followUp,
      status: "cancelled",
      scheduledAt: "",
      history: [
        ...(Array.isArray(followUp.history) ? followUp.history : []),
        {
          type: "cancelled",
          scheduledAt: followUp.scheduledAt,
          note: followUp.note || "",
          at: new Date().toISOString(),
          by: readActor(),
        },
      ].slice(-50),
    });
  };

  return (
    <PageWrap>
      <PageTitle>
        <div>
          <h1>{t("Calls")}</h1>
          <p>{t("Scheduled calls across all of your CRM boards")}</p>
        </div>
      </PageTitle>

      <CallsContent>
        <SummaryGrid>
          {[
            ["all", "Scheduled", summary.total],
            ["overdue", "Overdue", summary.overdue],
            ["today", "Today", summary.today],
            ["upcoming", "Upcoming", summary.upcoming],
          ].map(([value, label, count]) => (
            <SummaryCard key={value} active={timing === value} onClick={() => setTiming(value)}>
              <p>{t(label)}</p>
              <strong>{count || 0}</strong>
            </SummaryCard>
          ))}
        </SummaryGrid>

        <FilterBar>
          <FilterChip active={assignee === "all"} onClick={() => setAssignee("all")}>{t("All calls")}</FilterChip>
          <FilterChip active={assignee === "mine"} onClick={() => setAssignee("mine")}>{t("My calls")}</FilterChip>
        </FilterBar>

        {error ? <ErrorMessage>{t("Could not load calls right now")}</ErrorMessage> : null}
        {actionError && !activeAction ? <ErrorMessage>{actionError}</ErrorMessage> : null}
        {loading && !calls.length ? <Partial_Loader /> : null}

        {!loading && !calls.length && !error ? (
          <EmptyCalls>
            <IconSvg Icon="Phone" />
            <h3>{t("No scheduled calls")}</h3>
            <p>{t("Schedule a call from any lead and it will appear here.")}</p>
          </EmptyCalls>
        ) : null}

        {Object.entries(GROUP_LABELS).map(([state, label]) => groupedCalls[state].length ? (
          <CallGroup key={state}>
            <GroupTitle>
              <h3>{t(label)}</h3>
              <p>{groupedCalls[state].length}</p>
            </GroupTitle>
            {groupedCalls[state].map((call) => {
              const scheduledAt = new Date(call?.followUp?.scheduledAt);
              const lead = call?.lead || {};
              const phone = lead?.Tel || lead?.telephone || "";
              const leadId = String(lead?._id || lead?.id || "");
              const activeMode = activeAction?.leadId === leadId ? activeAction.mode : "";
              const previousOutcome = readPreviousOutcome(call?.followUp);
              return (
                <CallCard key={leadId} state={state}>
                  <DateTile>
                    <p>{scheduledAt.toLocaleDateString(undefined, { month: "short" })}</p>
                    <strong>{scheduledAt.toLocaleDateString(undefined, { day: "2-digit" })}</strong>
                  </DateTile>
                  <LeadDetails>
                    <h4 title={readLeadName(lead, t("New lead"))}>{readLeadName(lead, t("New lead"))}</h4>
                    <ContactLine>
                      <p>{scheduledAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p>
                      {phone ? <a href={`tel:${String(phone).replace(/\s/g, "")}`} title={phone}>{phone}</a> : null}
                    </ContactLine>
                    {call.columnTitle || (previousOutcome && CALL_OUTCOME_LABELS[previousOutcome]) ? (
                      <CallTags>
                        {call.columnTitle ? (
                          <ColumnTag
                            $columnColor={call.columnColor}
                            title={call.columnTitle}
                          >
                            {call.columnTitle}
                          </ColumnTag>
                        ) : null}
                        {previousOutcome && CALL_OUTCOME_LABELS[previousOutcome] ? (
                          <PreviousOutcomeTag $outcomeColor={CALL_OUTCOME_COLORS[previousOutcome]}>
                            {t("Last result")} · {t(CALL_OUTCOME_LABELS[previousOutcome])}
                          </PreviousOutcomeTag>
                        ) : null}
                      </CallTags>
                    ) : null}
                  </LeadDetails>
                  <Location>
                    <h5 title={call.boardName || t("CRM board")}>{call.boardName || t("CRM board")}</h5>
                    <AgendaField>
                      <AgendaText tabIndex="0" aria-label={call?.followUp?.note || t("No agenda")}>
                        {call?.followUp?.note || t("No agenda")}
                      </AgendaText>
                      <AgendaTooltip aria-hidden="true">
                        {call?.followUp?.note || t("No agenda")}
                      </AgendaTooltip>
                    </AgendaField>
                  </Location>
                  <CallActions>
                    {phone ? (
                      <Main_
                        text={t("Call now")}
                        iconLeft="Phone"
                        size="small"
                        onClick={() => { window.location.href = `tel:${String(phone).replace(/\s/g, "")}`; }}
                      />
                    ) : null}
                    <Main_Plain
                      text={t("Finish")}
                      iconLeft="Check"
                      noPadding
                    size="small"

                      disabled={savingAction}
                      onClick={() => openFinishCall(call)}
                    />
                    <CallMenuControl ref={openMenuLeadId === leadId ? callMenuRef : null}>
                      <Grey_
                        iconRight="More"
                        aria-label={t("More call actions")}
                    size="small"

                        disabled={savingAction}
                        onClick={() => setOpenMenuLeadId((current) => current === leadId ? "" : leadId)}
                      />
                      {openMenuLeadId === leadId ? (
                        <CallMenu>
                          <Grey_
                            text={t("Open board")}
                            iconRight="Next"
                            noPadding
                            onClick={() => {
                              setOpenMenuLeadId("");
                              openBoard(call);
                            }}
                          />
                          <Grey_ text={t("Edit call")} noPadding onClick={() => openEditCall(call)} />
                          <Red_ text={t("Cancel call")} onClick={() => cancelCall(call)} />
                        </CallMenu>
                      ) : null}
                    </CallMenuControl>
                  </CallActions>
                  {activeMode === "edit" ? (
                    <CallEditor>
                      <h4>{t("Edit call")}</h4>
                      <EditorFields>
                        <Builder_ title={useNativeDatePicker ? t("Date and time") : undefined}>
                          {useNativeDatePicker ? (
                            <NativeDateTimeInput
                              type="datetime-local"
                              value={toDateTimeLocalValue(editDraft.scheduledAt)}
                              min={toDateTimeLocalValue(new Date())}
                              onChange={(event) => {
                                const date = event.target.value ? new Date(event.target.value) : null;
                                setEditDraft((draft) => ({ ...draft, scheduledAt: date ? date.toISOString() : "" }));
                                setActionError("");
                              }}
                            />
                          ) : (
                            <Calendar_Field
                              label={t("Date and time")}
                              selected={editDraft.scheduledAt ? new Date(editDraft.scheduledAt) : null}
                              defaultDate={editDraft.scheduledAt ? new Date(editDraft.scheduledAt) : null}
                              onChange={(date) => {
                                setEditDraft((draft) => ({ ...draft, scheduledAt: date ? date.toISOString() : "" }));
                                setActionError("");
                              }}
                              placeholder={t("Choose date and time")}
                              interval={15}
                              minDate={new Date()}
                            />
                          )}
                        </Builder_>
                        <Builder_ title={t("Agenda")}>
                          <Textarea_
                            value={editDraft.note}
                            placeholder={t("What should you discuss?")}
                            onChange={(event) => setEditDraft((draft) => ({ ...draft, note: event.target.value }))}
                            rows={3}
                          />
                        </Builder_>
                      </EditorFields>
                      {actionError ? <ActionError role="alert">{actionError}</ActionError> : null}
                      <EditorActions>
                        <Main_ text={t("Save changes")} iconLeft="Check" disabled={savingAction} onClick={() => saveEditedCall(call)} />
                        <Grey_Link text={t("Cancel")} iconLeft="Xicon" noPadding onClick={() => { setActiveAction(null); setActionError(""); }} />
                      </EditorActions>
                    </CallEditor>
                  ) : null}
                  {activeMode === "finish" ? (
                    <CallEditor>
                      <h4>{t("How did the call go?")}</h4>
                      <OutcomeOptions>
                        {Object.entries(CALL_OUTCOME_LABELS).map(([value, outcomeLabel]) => (
                          <OutcomeOption
                            key={value}
                            type="button"
                            selected={outcomeDraft.outcome === value}
                            aria-pressed={outcomeDraft.outcome === value}
                            onClick={() => chooseOutcome(call, value)}
                          >
                            <p>{t(outcomeLabel)}</p>
                          </OutcomeOption>
                        ))}
                      </OutcomeOptions>
                      <Builder_ title={t("Call note")}>
                        <Textarea_
                          value={outcomeDraft.note}
                          placeholder={t("Add details from the call")}
                          onChange={(event) => setOutcomeDraft((draft) => ({ ...draft, note: event.target.value }))}
                          rows={3}
                        />
                      </Builder_>
                      {outcomeDraft.outcome && outcomeDraft.outcome !== "wrong_number" ? (
                        <Checkbox_
                          text={t("Schedule another call")}
                          checked={outcomeDraft.scheduleAnother}
                          onClick={() => setOutcomeDraft((draft) => {
                            const scheduleAnother = !draft.scheduleAnother;
                            return {
                              ...draft,
                              scheduleAnother,
                              scheduledAt: scheduleAnother ? draft.scheduledAt || suggestedNextCallDate(call) : "",
                            };
                          })}
                        />
                      ) : null}
                      {outcomeDraft.scheduleAnother ? (
                        <Builder_ title={useNativeDatePicker ? t("Call again") : undefined}>
                          {useNativeDatePicker ? (
                            <NativeDateTimeInput
                              type="datetime-local"
                              value={toDateTimeLocalValue(outcomeDraft.scheduledAt)}
                              min={toDateTimeLocalValue(new Date())}
                              onChange={(event) => {
                                const date = event.target.value ? new Date(event.target.value) : null;
                                setOutcomeDraft((draft) => ({ ...draft, scheduledAt: date ? date.toISOString() : "" }));
                                setActionError("");
                              }}
                            />
                          ) : (
                            <Calendar_Field
                              label={t("Call again")}
                              selected={outcomeDraft.scheduledAt ? new Date(outcomeDraft.scheduledAt) : null}
                              defaultDate={outcomeDraft.scheduledAt ? new Date(outcomeDraft.scheduledAt) : null}
                              onChange={(date) => {
                                setOutcomeDraft((draft) => ({ ...draft, scheduledAt: date ? date.toISOString() : "" }));
                                setActionError("");
                              }}
                              placeholder={t("Choose date and time")}
                              interval={15}
                              minDate={new Date()}
                            />
                          )}
                        </Builder_>
                      ) : null}
                      {actionError ? <ActionError role="alert">{actionError}</ActionError> : null}
                      <EditorActions>
                        <Main_ text={t("Save outcome")} iconLeft="Check" disabled={savingAction} onClick={() => finishCall(call)} />
                        <Grey_Link text={t("Cancel")} iconLeft="Xicon" noPadding onClick={() => { setActiveAction(null); setActionError(""); }} />
                      </EditorActions>
                    </CallEditor>
                  ) : null}
                </CallCard>
              );
            })}
          </CallGroup>
        ) : null)}

        {hasMore ? (
          <InfiniteScrollSentinel ref={loadMoreSentinelRef} aria-label={t("Loading more calls")}>
            {loading ? <Partial_Loader /> : null}
          </InfiniteScrollSentinel>
        ) : null}
      </CallsContent>
    </PageWrap>
  );
}
